/**
 * AIModelLearningReport Component
 * Comprehensive AI model performance and learning progress visualization
 * Features: Training metrics, feature importance, drift detection, model comparison
 */

import { useMemo, useState } from 'react'
import Plot from 'react-plotly.js'
import {
  Brain,
  TrendingUp,
  TrendingDown,
  Cpu,
  Database,
  RefreshCw,
  Award,
  AlertTriangle,
  CheckCircle,
  BarChart2,
  GitCompare,
  Layers,
  Sparkles,
  Clock
} from 'lucide-react'
import clsx from 'clsx'

interface ModelMetrics {
  accuracy: number
  precision: number
  recall: number
  f1Score: number
  auc: number
  mse?: number
}

interface TrainingHistory {
  epoch: number
  trainLoss: number
  valLoss: number
  accuracy: number
}

interface FeatureImportance {
  feature: string
  importance: number
  category: string
  trend: 'increasing' | 'decreasing' | 'stable'
}

interface ModelVersion {
  version: string
  timestamp: string
  metrics: ModelMetrics
  isActive: boolean
  improvement?: number
}

interface DriftMetric {
  feature: string
  driftScore: number
  threshold: number
  status: 'normal' | 'warning' | 'critical'
}

interface AIModelLearningReportProps {
  assetId?: string
  modelName: string
  modelType: 'foundation' | 'base' | 'per_asset'
  currentMetrics: ModelMetrics
  previousMetrics?: ModelMetrics
  trainingHistory: TrainingHistory[]
  featureImportance: FeatureImportance[]
  versions: ModelVersion[]
  driftMetrics?: DriftMetric[]
  lastTrainingDate: string
  dataPointsUsed: number
  className?: string
}

export default function AIModelLearningReport({
  assetId,
  modelName,
  modelType,
  currentMetrics,
  previousMetrics,
  trainingHistory,
  featureImportance,
  versions,
  driftMetrics,
  lastTrainingDate,
  dataPointsUsed,
  className
}: AIModelLearningReportProps) {
  const [activeTab, setActiveTab] = useState<'performance' | 'features' | 'versions' | 'drift'>('performance')

  const metricsChange = useMemo(() => {
    if (!previousMetrics) return null
    return {
      accuracy: currentMetrics.accuracy - previousMetrics.accuracy,
      precision: currentMetrics.precision - previousMetrics.precision,
      recall: currentMetrics.recall - previousMetrics.recall,
      f1Score: currentMetrics.f1Score - previousMetrics.f1Score,
      auc: currentMetrics.auc - previousMetrics.auc
    }
  }, [currentMetrics, previousMetrics])

  const getModelTypeStyle = () => {
    switch (modelType) {
      case 'foundation': return { color: '#8b5cf6', label: 'Foundation', icon: <Brain size={14} /> }
      case 'base': return { color: '#3b82f6', label: 'Base', icon: <Layers size={14} /> }
      case 'per_asset': return { color: '#10b981', label: 'Per-Asset', icon: <Cpu size={14} /> }
    }
  }

  const modelStyle = getModelTypeStyle()

  // Training loss chart
  const lossChartData = useMemo(() => [
    {
      type: 'scatter' as const,
      mode: 'lines' as const,
      name: 'Training Loss',
      x: trainingHistory.map(h => h.epoch),
      y: trainingHistory.map(h => h.trainLoss),
      line: { color: '#3b82f6', width: 2 }
    },
    {
      type: 'scatter' as const,
      mode: 'lines' as const,
      name: 'Validation Loss',
      x: trainingHistory.map(h => h.epoch),
      y: trainingHistory.map(h => h.valLoss),
      line: { color: '#f59e0b', width: 2, dash: 'dash' }
    }
  ], [trainingHistory])

  // Feature importance chart
  const featureChartData = useMemo(() => {
    const sorted = [...featureImportance].sort((a, b) => b.importance - a.importance).slice(0, 10)
    return [{
      type: 'bar' as const,
      orientation: 'h' as const,
      x: sorted.map(f => f.importance * 100),
      y: sorted.map(f => f.feature.slice(0, 25)),
      marker: {
        color: sorted.map(f => {
          if (f.trend === 'increasing') return '#10b981'
          if (f.trend === 'decreasing') return '#ef4444'
          return '#3b82f6'
        })
      },
      text: sorted.map(f => `${(f.importance * 100).toFixed(1)}%`),
      textposition: 'outside' as const,
      textfont: { color: '#94a3b8', size: 10 }
    }]
  }, [featureImportance])

  return (
    <div className={clsx(
      'rounded-2xl border border-surface-700 bg-gradient-to-br from-surface-900 via-surface-900 to-primary-950/10 overflow-hidden',
      className
    )}>
      {/* Header */}
      <div className="p-5 border-b border-surface-700 bg-gradient-to-r from-primary-500/10 via-purple-500/5 to-transparent">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl shadow-lg" style={{ background: `linear-gradient(135deg, ${modelStyle.color}, ${modelStyle.color}99)` }}>
              <Brain size={22} className="text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold text-surface-100">{modelName}</h3>
                <span className="px-2 py-0.5 text-xs rounded-full border" style={{
                  backgroundColor: `${modelStyle.color}20`,
                  borderColor: `${modelStyle.color}50`,
                  color: modelStyle.color
                }}>
                  {modelStyle.label} Model
                </span>
              </div>
              <p className="text-xs text-surface-400">
                {assetId && <><span className="font-mono text-primary-400">{assetId}</span> | </>}
                Last trained: {lastTrainingDate}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="text-right">
              <span className="text-xs text-surface-500">Training Data</span>
              <div className="flex items-center gap-1">
                <Database size={14} className="text-primary-400" />
                <span className="font-mono text-sm text-surface-200">{dataPointsUsed.toLocaleString()}</span>
              </div>
            </div>
            <div className="text-right">
              <span className="text-xs text-surface-500">Overall Accuracy</span>
              <p className={clsx(
                'font-mono text-xl font-bold',
                currentMetrics.accuracy >= 0.9 ? 'text-status-healthy' :
                currentMetrics.accuracy >= 0.8 ? 'text-status-warning' : 'text-status-critical'
              )}>
                {(currentMetrics.accuracy * 100).toFixed(1)}%
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b border-surface-700">
        {[
          { id: 'performance', label: 'Performance', icon: <BarChart2 size={14} /> },
          { id: 'features', label: 'Feature Importance', icon: <Sparkles size={14} /> },
          { id: 'versions', label: 'Version History', icon: <GitCompare size={14} /> },
          { id: 'drift', label: 'Drift Detection', icon: <RefreshCw size={14} /> }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={clsx(
              'flex items-center gap-2 px-4 py-3 text-sm font-medium transition-all',
              activeTab === tab.id
                ? 'text-primary-400 border-b-2 border-primary-400 bg-primary-500/5'
                : 'text-surface-400 hover:text-surface-200 hover:bg-surface-800/50'
            )}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="p-5">
        {activeTab === 'performance' && (
          <div className="space-y-6">
            {/* Metrics Grid */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {[
                { label: 'Accuracy', value: currentMetrics.accuracy, change: metricsChange?.accuracy },
                { label: 'Precision', value: currentMetrics.precision, change: metricsChange?.precision },
                { label: 'Recall', value: currentMetrics.recall, change: metricsChange?.recall },
                { label: 'F1 Score', value: currentMetrics.f1Score, change: metricsChange?.f1Score },
                { label: 'AUC-ROC', value: currentMetrics.auc, change: metricsChange?.auc }
              ].map((metric) => (
                <div key={metric.label} className="p-4 rounded-xl bg-surface-800/50 border border-surface-700">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-surface-400">{metric.label}</span>
                    {metric.change !== undefined && metric.change !== null && (
                      <span className={clsx(
                        'flex items-center gap-0.5 text-xs',
                        metric.change >= 0 ? 'text-status-healthy' : 'text-status-critical'
                      )}>
                        {metric.change >= 0 ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                        {(metric.change * 100).toFixed(1)}%
                      </span>
                    )}
                  </div>
                  <p className={clsx(
                    'text-2xl font-mono font-bold',
                    metric.value >= 0.9 ? 'text-status-healthy' :
                    metric.value >= 0.8 ? 'text-status-warning' : 'text-status-critical'
                  )}>
                    {(metric.value * 100).toFixed(1)}%
                  </p>
                </div>
              ))}
            </div>

            {/* Training Loss Chart */}
            <div>
              <h4 className="text-xs font-semibold text-surface-400 uppercase mb-3">Training History</h4>
              <Plot
                data={lossChartData as any}
                layout={{
                  height: 250,
                  margin: { t: 20, r: 20, b: 40, l: 50 },
                  paper_bgcolor: 'transparent',
                  plot_bgcolor: 'transparent',
                  font: { color: '#94a3b8', family: 'Inter, sans-serif', size: 10 },
                  xaxis: {
                    title: { text: 'Epoch' },
                    gridcolor: '#1e293b'
                  },
                  yaxis: {
                    title: { text: 'Loss' },
                    gridcolor: '#1e293b'
                  },
                  legend: {
                    orientation: 'h',
                    y: -0.2,
                    font: { size: 10 }
                  }
                }}
                config={{ displayModeBar: false, responsive: true }}
                style={{ width: '100%' }}
              />
            </div>
          </div>
        )}

        {activeTab === 'features' && (
          <div className="space-y-4">
            <Plot
              data={featureChartData as any}
              layout={{
                height: 400,
                margin: { t: 20, r: 60, b: 40, l: 180 },
                paper_bgcolor: 'transparent',
                plot_bgcolor: 'transparent',
                font: { color: '#94a3b8', family: 'Inter, sans-serif', size: 10 },
                xaxis: {
                  title: { text: 'Importance (%)' },
                  gridcolor: '#1e293b'
                },
                yaxis: {
                  autorange: 'reversed',
                  tickfont: { size: 11 }
                },
                showlegend: false,
                bargap: 0.3
              }}
              config={{ displayModeBar: false, responsive: true }}
              style={{ width: '100%' }}
            />

            {/* Feature Legend */}
            <div className="flex items-center justify-center gap-6 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-status-healthy" />
                <span className="text-surface-400">Increasing Importance</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-primary-500" />
                <span className="text-surface-400">Stable</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-status-critical" />
                <span className="text-surface-400">Decreasing Importance</span>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'versions' && (
          <div className="space-y-3">
            {versions.map((version) => (
              <div
                key={version.version}
                className={clsx(
                  'p-4 rounded-xl border transition-all',
                  version.isActive
                    ? 'border-status-healthy/50 bg-status-healthy/5'
                    : 'border-surface-700 bg-surface-800/30'
                )}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-sm font-semibold text-surface-200">{version.version}</span>
                    {version.isActive && (
                      <span className="px-2 py-0.5 text-xs rounded-full bg-status-healthy/20 text-status-healthy border border-status-healthy/30">
                        Active
                      </span>
                    )}
                    {version.improvement !== undefined && (
                      <span className={clsx(
                        'flex items-center gap-1 text-xs',
                        version.improvement >= 0 ? 'text-status-healthy' : 'text-status-critical'
                      )}>
                        {version.improvement >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                        {version.improvement >= 0 ? '+' : ''}{(version.improvement * 100).toFixed(2)}%
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-surface-500 flex items-center gap-1">
                    <Clock size={12} />
                    {version.timestamp}
                  </span>
                </div>
                <div className="grid grid-cols-5 gap-4 text-xs">
                  {['accuracy', 'precision', 'recall', 'f1Score', 'auc'].map((metric) => (
                    <div key={metric}>
                      <span className="text-surface-500 capitalize">{metric.replace('Score', '')}</span>
                      <p className="font-mono text-surface-300">
                        {(version.metrics[metric as keyof ModelMetrics] as number * 100).toFixed(1)}%
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'drift' && driftMetrics && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {driftMetrics.map((drift) => (
                <div
                  key={drift.feature}
                  className={clsx(
                    'p-4 rounded-xl border',
                    drift.status === 'critical' ? 'border-status-critical/50 bg-status-critical/5' :
                    drift.status === 'warning' ? 'border-status-warning/50 bg-status-warning/5' :
                    'border-surface-700 bg-surface-800/30'
                  )}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-surface-200">{drift.feature}</span>
                    <span className={clsx(
                      'px-2 py-0.5 text-xs rounded-full border',
                      drift.status === 'critical' ? 'bg-status-critical/20 text-status-critical border-status-critical/30' :
                      drift.status === 'warning' ? 'bg-status-warning/20 text-status-warning border-status-warning/30' :
                      'bg-status-healthy/20 text-status-healthy border-status-healthy/30'
                    )}>
                      {drift.status === 'critical' ? <AlertTriangle size={10} className="inline mr-1" /> :
                       drift.status === 'normal' ? <CheckCircle size={10} className="inline mr-1" /> : null}
                      {drift.status.toUpperCase()}
                    </span>
                  </div>
                  <div className="relative h-3 bg-surface-700 rounded-full overflow-hidden">
                    <div
                      className={clsx(
                        'absolute left-0 top-0 h-full rounded-full transition-all',
                        drift.status === 'critical' ? 'bg-status-critical' :
                        drift.status === 'warning' ? 'bg-status-warning' : 'bg-status-healthy'
                      )}
                      style={{ width: `${Math.min((drift.driftScore / drift.threshold) * 100, 100)}%` }}
                    />
                    <div
                      className="absolute top-0 bottom-0 w-0.5 bg-white/50"
                      style={{ left: '100%' }}
                    />
                  </div>
                  <div className="flex justify-between text-xs mt-1">
                    <span className="text-surface-400">Drift: <span className="font-mono">{drift.driftScore.toFixed(3)}</span></span>
                    <span className="text-surface-500">Threshold: {drift.threshold}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-5 py-3 bg-surface-800/30 border-t border-surface-700 flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs text-surface-500">
          <Brain size={12} />
          <span>Continuous Learning Pipeline - Auto-retraining enabled</span>
        </div>
        <div className="flex items-center gap-2">
          <Award size={12} className="text-primary-400" />
          <span className="text-xs text-surface-400">
            {versions.length} model versions | Best: v{versions.find(v => v.isActive)?.version}
          </span>
        </div>
      </div>
    </div>
  )
}
